import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  FileText, Plus, Download, Save, ArrowLeft, Calendar, User, Package,
  CheckSquare, PenTool, Eye, X, AlertTriangle, Minus, RefreshCw
} from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import jsPDF from 'jspdf';
import axios from 'axios';
import { usePermissions } from '../contexts/PermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;

const MaterialHandoverProtocol = ({ onBack }) => {
  const { isTechniker } = usePermissions();
  const [view, setView] = useState('list'); // 'list', 'create', 'view'
  const [protocols, setProtocols] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    handover_date: new Date().toISOString().slice(0, 16),
    employee_id: '',
    materials_with_quantities: [], // [{item_id: '', quantity: 1}]
    material_condition_confirmed: false,
    ownership_acknowledged: false,
    private_use_forbidden: false,
    notes: ''
  });

  const [selectedMaterials, setSelectedMaterials] = useState([]); // [{item_id: '', quantity: 1, item_details: {}}]
  const sigCanvas = useRef(null);
  const [selectedProtocol, setSelectedProtocol] = useState(null);

  useEffect(() => {
    if (view === 'list') {
      fetchProtocols();
    } else if (view === 'create') {
      loadCreateViewData();
    }
  }, [view]);

  const loadCreateViewData = async () => {
    // Load user first to get employee_id for technicians
    await fetchCurrentUser();
    // Then load other data
    await Promise.all([fetchEmployees(), fetchInventoryItems()]);
  };

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
      
      // Auto-fill employee_id for technicians
      if (response.data.role === 'techniker' && response.data.employee_id) {
        setFormData(prev => ({
          ...prev,
          employee_id: response.data.employee_id
        }));
      }
    } catch (error) {
      console.error('Fehler beim Laden des Benutzers:', error);
    }
  };

  const fetchProtocols = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/handover-protocols`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter for material protocols only
      setProtocols(response.data.filter(p => p.protocol_type === 'material'));
    } catch (error) {
      console.error('Fehler beim Laden der Protokolle:', error);
      alert('Fehler beim Laden der Protokolle');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.filter(emp => emp.status === 'aktiv'));
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventoryItems(response.data);
    } catch (error) {
      console.error('Fehler beim Laden des Inventars:', error);
    }
  };

  const addMaterial = () => {
    setSelectedMaterials([...selectedMaterials, { item_id: '', quantity: 1 }]);
  };

  const removeMaterial = (index) => {
    setSelectedMaterials(selectedMaterials.filter((_, i) => i !== index));
  };

  const updateMaterial = (index, field, value) => {
    const updated = [...selectedMaterials];
    updated[index] = { ...updated[index], [field]: value };
    setSelectedMaterials(updated);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.employee_id) {
      alert('Bitte wählen Sie einen Mitarbeiter aus');
      return;
    }
    if (selectedMaterials.length === 0 || selectedMaterials.some(m => !m.item_id || !m.quantity)) {
      alert('Bitte fügen Sie mindestens ein Material mit Menge hinzu');
      return;
    }
    if (!formData.material_condition_confirmed || !formData.ownership_acknowledged || !formData.private_use_forbidden) {
      alert('Bitte bestätigen Sie alle Bedingungen durch Setzen der Häkchen');
      return;
    }
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      alert('Bitte fügen Sie eine Unterschrift hinzu');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      // Get signature data
      const signatureData = sigCanvas.current.toDataURL();
      
      const protocolData = {
        protocol_type: "material",
        handover_date: new Date(formData.handover_date).toISOString(),
        employee_id: formData.employee_id,
        materials_with_quantities: selectedMaterials.map(m => ({
          item_id: m.item_id,
          quantity: parseInt(m.quantity)
        })),
        material_condition_confirmed: formData.material_condition_confirmed,
        ownership_acknowledged: formData.ownership_acknowledged,
        private_use_forbidden: formData.private_use_forbidden,
        signature_data: signatureData,
        notes: formData.notes
      };

      await axios.post(`${BACKEND_URL}/api/handover-protocols`, protocolData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Material-Übergabeprotokoll erfolgreich erstellt');
      setView('list');
      resetForm();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern des Protokolls');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      handover_date: new Date().toISOString().slice(0, 16),
      employee_id: '',
      materials_with_quantities: [],
      material_condition_confirmed: false,
      ownership_acknowledged: false,
      private_use_forbidden: false,
      notes: ''
    });
    setSelectedMaterials([]);
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const generatePDF = async (protocol) => {
    try {
      const pdf = new jsPDF();
      
      // Header
      pdf.setFontSize(20);
      pdf.text('SKALARTECH - Material-Übergabeprotokoll', 20, 30);
      
      // Protocol info
      pdf.setFontSize(12);
      pdf.text(`Protokoll-Nr.: ${protocol.protocol_number}`, 20, 50);
      pdf.text(`Datum: ${new Date(protocol.handover_date).toLocaleDateString('de-DE')}`, 20, 60);
      pdf.text(`Mitarbeiter: ${protocol.employee_name} (${protocol.employee_number})`, 20, 70);
      
      // Materials
      pdf.text('Übergebene Materialien:', 20, 90);
      let yPos = 100;
      if (protocol.materials_with_quantities) {
        protocol.materials_with_quantities.forEach((material, index) => {
          const details = material.item_details || {};
          const unit = details.unit || 'Stück';
          pdf.text(`${index + 1}. ${details.name || 'Unbekannt'} - Menge: ${material.quantity} ${unit}`, 25, yPos);
          if (details.category) {
            pdf.text(`   Kategorie: ${details.category}`, 25, yPos + 5);
            yPos += 5;
          }
          if (details.inventory_number) {
            pdf.text(`   Inventarnummer: ${details.inventory_number}`, 25, yPos + 5);
            yPos += 5;
          }
          yPos += 10;
        });
      }
      
      // Confirmation text
      yPos += 10;
      pdf.text('Bestätigungen:', 20, yPos);
      yPos += 10;
      if (protocol.confirmation_conditions) {
        protocol.confirmation_conditions.forEach((condition, index) => {
          const lines = pdf.splitTextToSize(`✓ ${condition}`, 170);
          pdf.text(lines, 20, yPos);
          yPos += lines.length * 5 + 3;
        });
      }
      yPos += 10;
      
      // Signature
      if (protocol.signature_data) {
        pdf.text('Unterschrift Mitarbeiter:', 20, yPos);
        yPos += 10;
        try {
          pdf.addImage(protocol.signature_data, 'PNG', 20, yPos, 80, 30);
        } catch (e) {
          pdf.text('Unterschrift vorhanden', 20, yPos);
        }
      }
      
      // Notes
      if (protocol.notes) {
        yPos += 40;
        pdf.text('Notizen:', 20, yPos);
        yPos += 10;
        const notesLines = pdf.splitTextToSize(protocol.notes, 170);
        pdf.text(notesLines, 20, yPos);
      }
      
      pdf.save(`Material-Übergabeprotokoll-${protocol.protocol_number}.pdf`);
    } catch (error) {
      console.error('Fehler beim PDF-Export:', error);
      alert('Fehler beim PDF-Export');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // List View
  if (view === 'list') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button onClick={onBack} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Lagerverwaltung
          </Button>
          <div className="flex gap-2">
            <Button 
              onClick={fetchProtocols}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Aktualisieren
            </Button>
            <Button 
              onClick={() => setView('create')}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Material-Übergabeprotokoll erstellen
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Material-Übergabeprotokolle ({protocols.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Laden...</div>
            ) : protocols.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Noch keine Material-Protokolle erstellt
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Protokoll-Nr.</th>
                      <th className="text-left p-3">Datum</th>
                      <th className="text-left p-3">Mitarbeiter</th>
                      <th className="text-left p-3">Anzahl Materialien</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {protocols.map((protocol) => (
                      <tr key={protocol.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{protocol.protocol_number}</td>
                        <td className="p-3">{formatDate(protocol.handover_date)}</td>
                        <td className="p-3">
                          {protocol.employee_name} 
                          <span className="text-gray-500 ml-1">({protocol.employee_number})</span>
                        </td>
                        <td className="p-3">
                          {protocol.materials_with_quantities ? protocol.materials_with_quantities.length : 0}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            protocol.status === 'active' ? 'bg-green-100 text-green-800' :
                            protocol.status === 'returned' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {protocol.status === 'active' ? 'Aktiv' :
                             protocol.status === 'returned' ? 'Zurückgegeben' : 'Storniert'}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedProtocol(protocol);
                                setView('view');
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => generatePDF(protocol)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Create View
  if (view === 'create') {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <Button onClick={() => setView('list')} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Liste
          </Button>
          <h2 className="text-xl font-bold">Material-Übergabeprotokoll erstellen</h2>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Material-Übergabeprotokoll erstellen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Date and Time */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Datum & Uhrzeit
              </label>
              <Input
                type="datetime-local"
                value={formData.handover_date}
                onChange={(e) => setFormData({...formData, handover_date: e.target.value})}
                className="w-full max-w-md"
              />
              <div className="text-sm text-gray-500 mt-1">
                Aktuell: {new Date().toLocaleString('de-DE')}
              </div>
            </div>

            {/* Employee Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <User className="w-4 h-4 inline mr-2" />
                {isTechniker() ? 'Mitarbeiter (Sie)' : 'Mitarbeiter auswählen (Empfänger der Materialien)'}
              </label>
              <select
                value={formData.employee_id}
                onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                disabled={isTechniker()}
                className={`w-full max-w-md p-2 border border-gray-300 rounded-md ${
                  isTechniker() ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Bitte wählen...</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name} ({employee.employee_number})
                  </option>
                ))}
              </select>
              {isTechniker() && formData.employee_id && (
                <p className="text-sm text-blue-600 mt-1">
                  ✓ Automatisch mit Ihrem Namen vorausgefüllt
                </p>
              )}
            </div>

            {/* Material Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <Package className="w-4 h-4 inline mr-2" />
                Materialauswahl ({selectedMaterials.length} Materialien)
              </label>
              
              {selectedMaterials.map((material, index) => (
                <div key={index} className="flex items-center gap-4 p-4 border border-gray-200 rounded-md mb-4">
                  <div className="flex-1">
                    <select
                      value={material.item_id}
                      onChange={(e) => updateMaterial(index, 'item_id', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Material auswählen...</option>
                      {inventoryItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name} ({item.category}) {item.inventory_number && `- ${item.inventory_number}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="w-32">
                    <label className="block text-xs text-gray-500 mb-1">Menge</label>
                    <Input
                      type="number"
                      min="1"
                      value={material.quantity}
                      onChange={(e) => updateMaterial(index, 'quantity', e.target.value)}
                      placeholder="Anzahl"
                      className="w-full"
                    />
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeMaterial(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              
              <Button
                variant="outline"
                onClick={addMaterial}
                className="flex items-center gap-2 mt-2"
              >
                <Plus className="w-4 h-4" />
                Material hinzufügen
              </Button>
            </div>

            {/* Employee Confirmation */}
            <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CheckSquare className="w-5 h-5" />
                Bestätigung des Mitarbeiters (alle erforderlich)
              </h3>
              
              <div className="space-y-3">
                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.material_condition_confirmed}
                    onChange={(e) => setFormData({...formData, material_condition_confirmed: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded mt-1"
                  />
                  <span className="text-sm">
                    Ich bestätige, dass ich alle Materialien in einwandfreiem Zustand erhalten habe.
                  </span>
                </label>
                
                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.ownership_acknowledged}
                    onChange={(e) => setFormData({...formData, ownership_acknowledged: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded mt-1"
                  />
                  <span className="text-sm">
                    Alle Materialien sind Eigentum der Firma SKALARTECH und müssen auf Aufforderung zurückgegeben werden.
                  </span>
                </label>
                
                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.private_use_forbidden}
                    onChange={(e) => setFormData({...formData, private_use_forbidden: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded mt-1"
                  />
                  <span className="text-sm">
                    Private Nutzung ist ausdrücklich untersagt.
                  </span>
                </label>
              </div>
            </div>

            {/* Digital Signature */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <PenTool className="w-4 h-4 inline mr-2" />
                Digitale Unterschrift des Mitarbeiters
              </label>
              <div className="border border-gray-300 rounded-md">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    width: 400,
                    height: 150,
                    className: 'signature-canvas'
                  }}
                />
                <div className="p-2 bg-gray-50 border-t flex justify-end">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => sigCanvas.current?.clear()}
                  >
                    Löschen
                  </Button>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Notizfeld (optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
                placeholder="Falls zusätzliche Anmerkungen nötig sind..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button 
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              >
                <Save className="w-4 h-4" />
                {loading ? 'Speichern...' : 'Speichern'}
              </Button>
              <Button 
                variant="outline"
                onClick={() => setView('list')}
                disabled={loading}
              >
                Abbrechen
              </Button>
              <Button 
                variant="outline"
                onClick={() => generatePDF({ 
                  protocol_number: 'VORSCHAU',
                  handover_date: formData.handover_date,
                  employee_name: employees.find(e => e.id === formData.employee_id)?.first_name + ' ' + employees.find(e => e.id === formData.employee_id)?.last_name || 'Unbekannt',
                  employee_number: employees.find(e => e.id === formData.employee_id)?.employee_number || '',
                  materials_with_quantities: selectedMaterials.map(m => ({
                    quantity: m.quantity,
                    item_details: inventoryItems.find(i => i.id === m.item_id) || { name: 'Unbekannt' }
                  })),
                  confirmation_conditions: [
                    formData.material_condition_confirmed ? 'Ich bestätige, dass ich alle Materialien in einwandfreiem Zustand erhalten habe.' : null,
                    formData.ownership_acknowledged ? 'Alle Materialien sind Eigentum der Firma SKALARTECH und müssen auf Aufforderung zurückgegeben werden.' : null,
                    formData.private_use_forbidden ? 'Private Nutzung ist ausdrücklich untersagt.' : null
                  ].filter(Boolean),
                  signature_data: sigCanvas.current && !sigCanvas.current.isEmpty() ? sigCanvas.current.toDataURL() : null,
                  notes: formData.notes
                })}
                className="flex items-center gap-2"
                disabled={!formData.employee_id || selectedMaterials.length === 0}
              >
                <Download className="w-4 h-4" />
                PDF-Vorschau
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // View Protocol Detail
  if (view === 'view' && selectedProtocol) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <Button onClick={() => setView('list')} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Liste
          </Button>
          <div className="flex gap-2">
            <Button 
              onClick={() => generatePDF(selectedProtocol)}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              PDF Export
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Material-Übergabeprotokoll: {selectedProtocol.protocol_number}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <strong>Datum & Uhrzeit:</strong>
                <div>{formatDate(selectedProtocol.handover_date)}</div>
              </div>
              <div>
                <strong>Mitarbeiter:</strong>
                <div>{selectedProtocol.employee_name} ({selectedProtocol.employee_number})</div>
              </div>
            </div>

            <div>
              <strong>Übergebene Materialien ({selectedProtocol.materials_with_quantities?.length || 0}):</strong>
              {selectedProtocol.materials_with_quantities && selectedProtocol.materials_with_quantities.length > 0 ? (
                <div className="mt-2">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 p-2 text-left">Material</th>
                        <th className="border border-gray-300 p-2 text-left">Kategorie</th>
                        <th className="border border-gray-300 p-2 text-left">Menge</th>
                        <th className="border border-gray-300 p-2 text-left">Inventarnummer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProtocol.materials_with_quantities.map((material, index) => {
                        const details = material.item_details || {};
                        return (
                          <tr key={index}>
                            <td className="border border-gray-300 p-2">{details.name || 'Unbekannt'}</td>
                            <td className="border border-gray-300 p-2">{details.category || '-'}</td>
                            <td className="border border-gray-300 p-2">{material.quantity} {details.unit || 'Stück'}</td>
                            <td className="border border-gray-300 p-2">{details.inventory_number || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-gray-500 mt-2">Keine Materialien aufgeführt</div>
              )}
            </div>

            <div>
              <strong>Bestätigungen:</strong>
              {selectedProtocol.confirmation_conditions && selectedProtocol.confirmation_conditions.length > 0 ? (
                <ul className="list-disc ml-6 mt-2">
                  {selectedProtocol.confirmation_conditions.map((condition, index) => (
                    <li key={index} className="text-green-700">✓ {condition}</li>
                  ))}
                </ul>
              ) : (
                <div className="text-gray-500 mt-2">Keine Bestätigungen aufgeführt</div>
              )}
            </div>

            {selectedProtocol.signature_data && (
              <div>
                <strong>Unterschrift:</strong>
                <div className="mt-2 border rounded p-2 bg-white">
                  <img 
                    src={selectedProtocol.signature_data} 
                    alt="Unterschrift" 
                    className="max-w-md border"
                  />
                </div>
              </div>
            )}

            {selectedProtocol.notes && (
              <div>
                <strong>Notizen:</strong>
                <div className="mt-2 p-3 bg-gray-50 rounded border">
                  {selectedProtocol.notes}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <strong>Status:</strong> {selectedProtocol.status === 'active' ? 'Aktiv' : selectedProtocol.status}
              </div>
              <div>
                <strong>Erstellt:</strong> {formatDate(selectedProtocol.created_at)}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default MaterialHandoverProtocol;